import type { RangeValue } from "@similarweb/pro-logic-components/dist/filter-factory/src/compound/types/type-utils";

import { translate } from "app/filters/i18nFilter";
import {
    CitationGapStatus,
    CitationStatus,
    type SortDirection,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import type { ITableState } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/tableReducer";

import type { Prompt, IPromptsFilterState } from "./types";

export const FETCH_PAGE_SIZE = 5000;
export const PAGE_SIZE = 100;
export const EXCEL_API_ENDPOINT = "/api/aiTracker/reports/promptsReport/PromptsTable/Excel/v2";
export const PROMPTS_TABLE_RETURN_LAST_DATE = true;
export const PROMPTS_TABLE_SORT_FIELD = "prompt";
export const PROMPTS_TABLE_SORT_ASC = true;
export const VISIBILITY_RANGE: RangeValue = {
    from: 0,
    to: 100,
};

export const COLUMN_KEYS = {
    prompt: "prompt",
    visibility: "visibility",
    popChange: "popChange",
    mentionSentimentStatus: "mentionSentimentStatus",
    averagePosition: "averagePosition",
    sentimentTone: "sentimentTone",
    topBrands: "topBrands",
    topSourceCategories: "topSourceCategories",
    citationsWithInfluence: "citationsWithInfluence",
    isCitedLatest: "isCitedLatest",
    citationGap: "citationGap",
    citationVolatility: "citationVolatility",
    topics: "topics",
    intent: "intent",
} as const satisfies Record<string, keyof Prompt>;

export const DEFAULT_SORT_COLUMN: keyof Prompt = "visibility";
export const DEFAULT_SORT_DIRECTION: SortDirection = "descend";
export const SORTABLE_COLUMN_KEYS: ReadonlyArray<keyof Prompt> = Object.values(COLUMN_KEYS);

export type QuickFilterId = "mention-wins" | "mention-gap" | "citation-wins" | "citation-gap";

export const MENTION_GAP_MIN_VISIBILITY = 0;
export const MENTION_GAP_MAX_VISIBILITY = 5;

export type PromptsDataFilterUrlParams = {
    mentionStatuses?: string;
    minVisibility?: string;
    maxVisibility?: string;
    brands?: string;
    domains?: string;
    topSourceCategories?: string;
    citationGaps?: string;
    citationVolatility?: string;
    sentimentTone?: string;
    latestCitationStatuses?: string;
    searchTerm?: string;
    topicsFilter?: string;
    coreCompetitors?: string;
    change?: string;
    sentimentShift?: string;
};

interface IQuickFilterConfig {
    preset: PromptsDataFilterUrlParams;
    disabledWithoutCompetitors?: boolean;
}

export const MENTION_WIN_STATUS = "mentioned";

export const QUICK_FILTER_CONFIGS: Record<QuickFilterId, IQuickFilterConfig> = {
    "mention-wins": {
        preset: { mentionStatuses: MENTION_WIN_STATUS },
    },
    "mention-gap": {
        preset: {
            minVisibility: String(MENTION_GAP_MIN_VISIBILITY),
            maxVisibility: String(MENTION_GAP_MAX_VISIBILITY),
            coreCompetitors: "true",
        },
        disabledWithoutCompetitors: true,
    },
    "citation-wins": {
        preset: { latestCitationStatuses: CitationStatus.Cited },
    },
    "citation-gap": {
        preset: { citationGaps: [CitationGapStatus.Full, CitationGapStatus.Partial].join(",") },
        disabledWithoutCompetitors: true,
    },
};

export const QUICK_FILTER_NAMES: Record<QuickFilterId, string> = {
    get "mention-wins"() {
        return translate("llm.prompts.quick_filters.mention_wins.title");
    },
    get "mention-gap"() {
        return translate("llm.prompts.quick_filters.mention_gap.title");
    },
    get "citation-wins"() {
        return translate("llm.prompts.quick_filters.citation_wins.title");
    },
    get "citation-gap"() {
        return translate("llm.prompts.quick_filters.citation_gap.title");
    },
};

export const QUICK_FILTER_TRACKING_CATEGORY = "LLM Tracker - Prompts Quick Filter";

export const CLEARED_DATA_FILTER_URL_PARAMS: Record<keyof PromptsDataFilterUrlParams, undefined> = {
    mentionStatuses: undefined,
    sentimentTone: undefined,
    minVisibility: undefined,
    maxVisibility: undefined,
    brands: undefined,
    domains: undefined,
    topSourceCategories: undefined,
    citationGaps: undefined,
    citationVolatility: undefined,
    latestCitationStatuses: undefined,
    searchTerm: undefined,
    topicsFilter: undefined,
    coreCompetitors: undefined,
    change: undefined,
    sentimentShift: undefined,
};

export const INITIAL_TABLE_STATE: ITableState<IPromptsFilterState, keyof Prompt> = {
    currentPage: 1,
    searchTerm: "",
    filterState: {},
    sorting: {
        dataIndex: DEFAULT_SORT_COLUMN,
        sortDirection: "descend",
    },
};
